import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Clock,
  ChefHat,
  Calendar,
  User,
  Sparkles,
  X,
  Filter,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { useSemanticSearch } from "../../../../hooks/useSemanticSearch";
import { trackEvent } from "../../../config/analytics";

interface SearchResult {
  id: string;
  type: "recipe" | "meal-plan" | "ingredient" | "user" | "tip";
  title: string;
  subtitle?: string;
  image?: string;
  tags?: string[];
  lastUsed?: string;
}

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (result: SearchResult) => void;
  placeholder?: string;
}

// Hook to get real search data via semantic search
const useSearchData = (query: string, filters: string[]) => {
  const { recipes, updateQuery, isLoading, hasQuery } = useSemanticSearch({
    query,
    limit: 15,
    enabled: true,
    debounceMs: 300,
  });

  useEffect(() => {
    updateQuery(query);
    // Only depend on query; updateQuery is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const mapped: SearchResult[] = hasQuery
    ? recipes.map((r: any) => ({
        id: String(r?.recipe?.id ?? r?.recipe?._id ?? r?.recipe?.slug ?? ""),
        type: "recipe",
        title: r?.recipe?.title ?? r?.recipe?.name ?? "Recipe",
        subtitle: r?.recipe?.summary ?? r?.recipe?.subtitle ?? undefined,
        image: r?.recipe?.imageUrl ?? r?.recipe?.image ?? undefined,
        tags: r?.matchingFactors ?? [],
      }))
    : getRecentItems();

  const wantsRecipes = filters.length === 0 || filters.includes("recipes");
  const results = wantsRecipes ? mapped : [];
  return { results, isLoading };
};

const getRecentItems = (): SearchResult[] => {
  try {
    const recent = localStorage.getItem("nura_recent_searches");
    return recent ? JSON.parse(recent) : demoResults.slice(0, 5);
  } catch {
    return demoResults.slice(0, 5);
  }
};

const saveRecentItem = (item: SearchResult) => {
  try {
    const recent = getRecentItems();
    const filtered = recent.filter((r) => r.id !== item.id);
    const updated = [{ ...item, lastUsed: "just now" }, ...filtered].slice(
      0,
      10,
    );
    localStorage.setItem("nura_recent_searches", JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent item:", error);
  }
};

// Small demo list when opening without a query
const demoResults: SearchResult[] = [
  {
    id: "demo-1",
    type: "recipe",
    title: "Mediterranean Quinoa Bowl",
    subtitle: "25 min • Easy",
    image: "/api/placeholder/60/60",
    tags: ["Mediterranean"],
  },
  {
    id: "demo-2",
    type: "recipe",
    title: "Honey Garlic Salmon",
    subtitle: "20 min • Seafood",
    image: "/api/placeholder/60/60",
    tags: ["Seafood"],
  },
];

const getTypeIcon = (type: SearchResult["type"]) => {
  switch (type) {
    case "recipe":
      return <ChefHat className="h-4 w-4" />;
    case "meal-plan":
      return <Calendar className="h-4 w-4" />;
    case "ingredient":
      return <Search className="h-4 w-4" />;
    case "user":
      return <User className="h-4 w-4" />;
    case "tip":
      return <Sparkles className="h-4 w-4" />;
    default:
      return <Search className="h-4 w-4" />;
  }
};

const getTypeColor = (type: SearchResult["type"]) => {
  switch (type) {
    case "recipe":
      return "bg-coral-100 text-coral-800 border-coral-200";
    case "meal-plan":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "ingredient":
      return "bg-green-100 text-green-800 border-green-200";
    case "user":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "tip":
      return "bg-pink-100 text-pink-800 border-pink-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export function GlobalSearchModal({
  isOpen,
  onClose,
  onSelect,
  placeholder = "Search recipes, meal plans, ingredients...",
}: GlobalSearchModalProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Use real search hook
  const { results, isLoading } = useSearchData(query, activeFilters);

  // Analytics: track open/close
  const wasOpenRef = useRef<boolean>(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      try {
        trackEvent("search_open");
      } catch {}
      wasOpenRef.current = true;
    } else if (!isOpen && wasOpenRef.current) {
      try {
        trackEvent("search_close");
      } catch {}
      wasOpenRef.current = false;
    }
  }, [isOpen]);

  // Analytics: track query updates (debounced)
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const q = (query || "").trim();
      const len = q.length;
      try {
        trackEvent("search_query", {
          len,
          q: q.slice(0, 64),
          filters: activeFilters,
        });
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [query, activeFilters, isOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (results[selectedIndex]) {
            const selected = results[selectedIndex];
            try {
              trackEvent("search_select", {
                id: selected.id,
                type: selected.type,
              });
            } catch {}
            saveRecentItem(selected);
            onSelect(selected);
            onClose();
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, results, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  const toggleFilter = (filter: string) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
  };

  const clearFilters = () => {
    setActiveFilters([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      <div className="fixed inset-x-4 top-20 md:inset-x-auto md:left-1/2 md:w-full md:max-w-2xl md:-translate-x-1/2">
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
          {/* Search Header */}
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
            <Search className="h-5 w-5 flex-shrink-0 text-gray-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="border-0 bg-transparent text-lg focus:ring-0"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex-shrink-0 p-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Filter Bar */}
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">
                Filters:
              </span>
              {["recipes", "meal-plans", "ingredients", "tips"].map(
                (filter) => (
                  <Badge
                    key={filter}
                    variant={
                      activeFilters.includes(filter) ? "default" : "outline"
                    }
                    className="cursor-pointer text-xs capitalize"
                    onClick={() => toggleFilter(filter)}
                  >
                    {filter}
                  </Badge>
                ),
              )}
              {activeFilters.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-6 px-2 text-xs text-gray-500"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto" ref={resultsRef}>
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-warning-500"></div>
                <p>Searching...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                {query ? (
                  <>
                    <Search className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                    <p>No results found for "{query}"</p>
                    <p className="mt-1 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </>
                ) : (
                  <>
                    <Clock className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                    <p>Start typing to search</p>
                    <p className="mt-1 text-sm">
                      Search recipes, meal plans, and more
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div>
                {!query && (
                  <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500">
                    RECENT
                  </div>
                )}
                {results.map((result, index) => (
                  <div
                    key={result.id}
                    className={`cursor-pointer px-4 py-3 transition-colors ${
                      index === selectedIndex
                        ? "border-l-2 border-coral-500 bg-coral-50"
                        : "desktop-hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      try {
                        trackEvent("search_select", {
                          id: result.id,
                          type: result.type,
                        });
                      } catch {}
                      saveRecentItem(result);
                      onSelect(result);
                      onClose();
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {result.image ? (
                        <img
                          src={result.image}
                          alt={result.title}
                          className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-lg ${getTypeColor(result.type)}`}
                        >
                          {getTypeIcon(result.type)}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <h3 className="truncate font-medium text-gray-900">
                            {result.title}
                          </h3>
                          <Badge
                            variant="outline"
                            className={`text-xs ${getTypeColor(result.type)}`}
                          >
                            {result.type}
                          </Badge>
                        </div>

                        {result.subtitle && (
                          <p className="truncate text-sm text-gray-500">
                            {result.subtitle}
                          </p>
                        )}

                        <div className="mt-1 flex items-center gap-2">
                          {result.tags?.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {result.lastUsed && (
                            <span className="ml-auto flex items-center gap-1 text-xs text-gray-400">
                              <Clock className="h-3 w-3" />
                              {result.lastUsed}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs">
                ↑↓
              </kbd>
              <span>Navigate</span>
              <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs">
                ⏎
              </kbd>
              <span>Select</span>
            </div>
            <div>
              <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs">
                Esc
              </kbd>
              <span className="ml-1">Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
